import { Tooltip, UnstyledButton } from '@mantine/core'
import type { ReactNode } from 'react'
import { useLinkComponent } from '../link'
import classes from './SegmentBar.module.css'

export interface Segment {
  key: string
  label: string
  value: number
  /** CSS colour, usually `var(--tnn-ramp-N)` for ordered stages */
  color: string
  href?: string
}

export interface SegmentBarProps {
  segments: Segment[]
  /** Accessible name of the whole bar */
  label: string
  formatValue?: (n: number) => ReactNode
}

/**
 * Part-to-whole bar for ordered stages (a pipeline). Each segment is its own
 * hover/focus target with a tooltip; the legend below repeats every label and
 * count, so nothing depends on colour or on hovering.
 */
export function SegmentBar({ segments, label, formatValue = (n) => n.toLocaleString() }: SegmentBarProps) {
  const Link = useLinkComponent()
  const total = segments.reduce((sum, s) => sum + s.value, 0)
  const visible = segments.filter((s) => s.value > 0)
  return (
    <div className={classes.root}>
      <div className={classes.bar} role="img" aria-label={label}>
        {visible.map((s) => (
          <Tooltip key={s.key} label={`${s.label} · ${s.value.toLocaleString()}`} withArrow>
            <UnstyledButton
              component={(s.href ? Link : 'span') as 'a'}
              href={s.href}
              className={classes.segment}
              style={{ flexGrow: s.value, background: s.color }}
              aria-label={`${s.label} ${s.value}`}
              tabIndex={0}
            />
          </Tooltip>
        ))}
        {total === 0 && <span className={classes.empty} />}
      </div>
      <ul className={classes.legend}>
        {segments.map((s) => (
          <li key={s.key}>
            <UnstyledButton component={(s.href ? Link : 'span') as 'a'} href={s.href} className={classes.item}>
              <span className={classes.swatch} style={{ background: s.color }} />
              <span className={classes.itemLabel}>{s.label}</span>
              <span className={classes.itemValue}>{formatValue(s.value)}</span>
            </UnstyledButton>
          </li>
        ))}
      </ul>
    </div>
  )
}
